/**
 * Encompass Fee Service
 *
 * Frontend service for managing Encompass itemization fees.
 * Provides methods to fetch, search, and sync fees from Encompass.
 */

import { supabase } from '@/utils/supabase';
import {
  EncompassItemizationFee,
  EncompassItemizationFeeRow,
  FeeSyncLog,
  FeeSyncLogRow,
  FeeSyncResult,
  FeeStatusResponse,
  LESection,
  transformFeeRow,
  transformSyncLogRow
} from '@/types/encompass-fees';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('EncompassFeeService', 'fees');

/**
 * Fetch all active fees
 */
export async function getFees(): Promise<EncompassItemizationFee[]> {
  log.info('Fetching all active fees');

  const { data, error } = await supabase
    .from('encompass_itemization_fees')
    .select('*')
    .eq('is_active', true)
    .order('name');

  if (error) {
    log.error('Failed to fetch fees', { error: error.message });
    throw new Error(`Failed to fetch fees: ${error.message}`);
  }

  return (data as EncompassItemizationFeeRow[]).map(transformFeeRow);
}

/**
 * Fetch fees grouped by section
 */
export async function getFeesBySection(): Promise<Record<LESection, EncompassItemizationFee[]>> {
  log.info('Fetching fees grouped by section');

  const fees = await getFees();

  const grouped: Record<LESection, EncompassItemizationFee[]> = {
    Section800: [],
    Section900: [],
    Section1000: [],
    Section1100: [],
    Section1200: [],
    Section1300: []
  };

  for (const fee of fees) {
    for (const section of fee.sections) {
      if (section in grouped) {
        grouped[section as LESection].push(fee);
      }
    }
  }

  return grouped;
}

/**
 * Fetch fees for a specific section
 */
export async function getFeesForSection(section: LESection): Promise<EncompassItemizationFee[]> {
  log.info('Fetching fees for section', { section });

  const { data, error } = await supabase
    .from('encompass_itemization_fees')
    .select('*')
    .eq('is_active', true)
    .contains('sections', [section])
    .order('name');

  if (error) {
    log.error('Failed to fetch fees for section', { section, error: error.message });
    throw new Error(`Failed to fetch fees for section: ${error.message}`);
  }

  return (data as EncompassItemizationFeeRow[]).map(transformFeeRow);
}

/**
 * Search fees by name, UCD code, or Encompass ID
 */
export async function searchFees(query: string): Promise<EncompassItemizationFee[]> {
  log.info('Searching fees', { query });

  const { data, error } = await supabase
    .from('encompass_itemization_fees')
    .select('*')
    .eq('is_active', true)
    .or(`name.ilike.%${query}%,ucd_fee.ilike.%${query}%,encompass_fee_id.ilike.%${query}%`)
    .order('name')
    .limit(50);

  if (error) {
    log.error('Failed to search fees', { query, error: error.message });
    throw new Error(`Failed to search fees: ${error.message}`);
  }

  return (data as EncompassItemizationFeeRow[]).map(transformFeeRow);
}

/**
 * Get fee by ID
 */
export async function getFeeById(id: string): Promise<EncompassItemizationFee | null> {
  log.info('Fetching fee by ID', { id });

  const { data, error } = await supabase
    .from('encompass_itemization_fees')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Not found
    }
    log.error('Failed to fetch fee', { id, error: error.message });
    throw new Error(`Failed to fetch fee: ${error.message}`);
  }

  return transformFeeRow(data as EncompassItemizationFeeRow);
}

/**
 * Get fee counts
 */
export async function getFeeCounts(): Promise<{ total: number; active: number }> {
  log.info('Fetching fee counts');

  const { count: total, error: totalError } = await supabase
    .from('encompass_itemization_fees')
    .select('*', { count: 'exact', head: true });

  if (totalError) {
    log.error('Failed to get total fee count', { error: totalError.message });
    throw new Error(`Failed to get fee count: ${totalError.message}`);
  }

  const { count: active, error: activeError } = await supabase
    .from('encompass_itemization_fees')
    .select('*', { count: 'exact', head: true })
    .eq('is_active', true);

  if (activeError) {
    log.error('Failed to get active fee count', { error: activeError.message });
    throw new Error(`Failed to get fee count: ${activeError.message}`);
  }

  return { total: total || 0, active: active || 0 };
}

/**
 * Get section counts
 */
export async function getSectionCounts(): Promise<Record<LESection, number>> {
  log.info('Fetching section counts');

  const fees = await getFees();

  const counts: Record<LESection, number> = {
    Section800: 0,
    Section900: 0,
    Section1000: 0,
    Section1100: 0,
    Section1200: 0,
    Section1300: 0
  };

  for (const fee of fees) {
    for (const section of fee.sections) {
      if (section in counts) {
        counts[section as LESection]++;
      }
    }
  }

  return counts;
}

/**
 * Get fee status (counts and last sync)
 */
export async function getFeeStatus(): Promise<FeeStatusResponse> {
  log.info('Fetching fee status');

  const { total, active } = await getFeeCounts();

  const { data: lastSync, error } = await supabase
    .from('fee_sync_log')
    .select('*')
    .order('started_at', { ascending: false })
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') {
    log.error('Failed to get last sync', { error: error.message });
  }

  return {
    totalFees: total,
    activeFees: active,
    lastSync: lastSync ? transformSyncLogRow(lastSync as FeeSyncLogRow) : null
  };
}

/**
 * Get sync history
 */
export async function getSyncHistory(limit: number = 10): Promise<FeeSyncLog[]> {
  log.info('Fetching sync history', { limit });

  const { data, error } = await supabase
    .from('fee_sync_log')
    .select('*')
    .order('started_at', { ascending: false })
    .limit(limit);

  if (error) {
    log.error('Failed to fetch sync history', { error: error.message });
    throw new Error(`Failed to fetch sync history: ${error.message}`);
  }

  return (data as FeeSyncLogRow[]).map(transformSyncLogRow);
}

/**
 * Trigger fee sync from Encompass (requires admin permissions)
 */
export async function triggerSync(): Promise<FeeSyncResult> {
  log.info('Triggering fee sync');

  const { data, error } = await supabase.functions.invoke('sync-encompass-fees', {
    body: { action: 'sync' }
  });

  if (error) {
    log.error('Failed to trigger sync', { error: error.message });
    throw new Error(`Failed to trigger sync: ${error.message}`);
  }

  if (!data.success) {
    log.error('Sync failed', { error: data.error });
    throw new Error(data.error || 'Sync failed');
  }

  log.info('Sync completed', {
    feesAdded: data.feesAdded,
    feesUpdated: data.feesUpdated,
    feesRemoved: data.feesRemoved
  });

  return data as FeeSyncResult;
}

/**
 * Get fees via edge function (alternative to direct Supabase query)
 */
export async function getFeesViaEdgeFunction(options?: {
  section?: LESection;
  search?: string;
}): Promise<EncompassItemizationFee[]> {
  log.info('Fetching fees via edge function', options);

  const { data, error } = await supabase.functions.invoke('sync-encompass-fees', {
    body: {
      action: 'fees',
      section: options?.section,
      search: options?.search
    }
  });

  if (error) {
    log.error('Failed to fetch fees', { error: error.message });
    throw new Error(`Failed to fetch fees: ${error.message}`);
  }

  // Edge function returns snake_case data
  return (data.fees as EncompassItemizationFeeRow[]).map(transformFeeRow);
}

/**
 * Export service functions
 */
export const encompassFeeService = {
  getFees,
  getFeesBySection,
  getFeesForSection,
  searchFees,
  getFeeById,
  getFeeCounts,
  getSectionCounts,
  getFeeStatus,
  getSyncHistory,
  triggerSync,
  getFeesViaEdgeFunction
};

export default encompassFeeService;
